import { DeliveryMethodMap, normalizeModuleId, parseIdList } from "./moduleHelpers";

/**
 * Convert year to numeric value for sorting
 */
const getYearNumeric = (year) => {
    if (typeof year === "number") return year;
    const yearMap = { "First": 1, "Second": 2, "Third": 3, "Fourth": 4, "1": 1, "2": 2, "3": 3, "4": 4 };
    return yearMap[year] || 999;
};

/**
 * Convert term to numeric value for sorting (year modules "1, 2" come before semester 1)
 */
const getTermNumeric = (term) => {
    if (term === "1, 2" || term === "1,2") return 0.5; // Year modules sort before semester 1
    const termNum = Number(term);
    return isNaN(termNum) ? 999 : termNum;
};

/**
 * Flag modules marked as inactive so they are excluded from auto-selection.
 * The API can surface this on either module_status or status/reason.
 */
const isInactiveModule = (module = {}) => {
    const normalizeStatusText = (value) => String(value || "").trim().toLowerCase();
    const isInactiveStatusText = (statusText) => {
        if (!statusText) return false;
        return /\binactive\b/.test(statusText) || /\bupcoming\b/.test(statusText);
    };

    const moduleStatusText = normalizeStatusText(module.module_status);
    const statusText = normalizeStatusText(module.status);
    const reasonText = normalizeStatusText(module.reason);

    return (
        isInactiveStatusText(moduleStatusText) ||
        isInactiveStatusText(statusText) ||
        isInactiveStatusText(reasonText)
    );
};

const filterActiveModules = (modules = []) => {
    return (modules || []).filter((module) => !isInactiveModule(module));
};

const getActiveModuleCount = (redoModules = [], progressionModules = []) => {
    const activeRedo = filterActiveModules(redoModules).length;
    const activeProgression = filterActiveModules(progressionModules).length;
    return activeRedo + activeProgression;
};

export const countActiveRedoProgressionModules = (redoModules = [], progressionModules = []) => {
    return getActiveModuleCount(redoModules, progressionModules);
};

/**
 * Ensure the max module cap never exceeds available active modules.
 */
export const capMaxByActiveModules = (maxModules = 0, redoModules = [], progressionModules = []) => {
    const activeCount = getActiveModuleCount(redoModules, progressionModules);
    if (maxModules > 0 && activeCount > 0) {
        return Math.min(maxModules, activeCount);
    }
    return maxModules;
};

/**
 * Sort modules by year (earliest first), then by term (earliest first)
 */
const sortModulesByEarliestTerm = (modules) => {
    return [...modules].sort((a, b) => {
        const yearA = getYearNumeric(a.year);
        const yearB = getYearNumeric(b.year);
        
        if (yearA !== yearB) {
            return yearA - yearB;
        }
        
        const termA = getTermNumeric(a.term);
        const termB = getTermNumeric(b.term);
        
        return termA - termB;
    });
};

/**
 * Auto-select PMC modules up to the allowed maximum:
 * - Prioritise redo (failed) modules
 * - Then fill with progression modules
 * - Within each category, prioritize by earliest year and term
 * - Never exceed the maxModules cap
 * - Returns arrays of netsuite_product_id values
 */
export const getPmcAutoSelection = ({
    redoModules = [],
    progressionModules = [],
    maxModules = 0,
    hasExistingSelection = false,
    readOnly = false
}) => {
    if (readOnly || hasExistingSelection) {
        return {
            selectedRedo: [],
            selectedProgression: []
        };
    }

    // Exclude inactive modules and then sort by earliest year and term
    const sortedRedoModules = sortModulesByEarliestTerm(filterActiveModules(redoModules));
    const sortedProgressionModules = sortModulesByEarliestTerm(filterActiveModules(progressionModules));

    const cappedMax = capMaxByActiveModules(maxModules, redoModules, progressionModules);
    const maxSelectable = cappedMax > 0 ? cappedMax : undefined;
    const selectedIds = [];

    const tryAdd = (id) => {
        if (maxSelectable && selectedIds.length >= maxSelectable) {
            return;
        }
        if (!selectedIds.includes(id)) {
            selectedIds.push(id);
        }
    };

    // Add redo modules first (already sorted by earliest term/year)
    sortedRedoModules.forEach(m => tryAdd(m.netsuite_product_id));
    
    // Then add progression modules (already sorted by earliest term/year)
    sortedProgressionModules.forEach(m => tryAdd(m.netsuite_product_id));

    const selectedRedo = sortedRedoModules
        .map(m => m.netsuite_product_id)
        .filter(id => selectedIds.includes(id));
    
    const selectedProgression = sortedProgressionModules
        .map(m => m.netsuite_product_id)
        .filter(id => selectedIds.includes(id) && !selectedRedo.includes(id));

    return {
        selectedRedo,
        selectedProgression
    };
};

/**
 * Build a sidebar-friendly module summary from PMC progression response data.
 * This is used on the Overview tab to ensure the module count / amounts and
 * qualification name are already visible before the student opens the Application tab.
 */
export const buildPmcSidebarSummary = ({
    responseData,
    qualificationOverride,
    readOnly = false,
    registration,
    readOnlyData
}) => {
    if (!responseData) {
        return null;
    }

    const qualification = qualificationOverride || responseData.qualification || { name: "", type: "" };
    const allModules = responseData.AllModules || [];

    const deliveryMethodMapped = DeliveryMethodMap[qualification?.type] ?? 1;
    
    const allTransferModules = responseData?.transferModules || [];
    const transferModulesSummaryPartition = allTransferModules.reduce(
        (acc, module) => {
            const excludedReasons = [];
            if (module?.isDeReg !== false) excludedReasons.push("deregistered");
            if (module?.failed !== false) excludedReasons.push("failed");
            if (module?.optional !== 1) excludedReasons.push("notOptional");

            if (excludedReasons.length === 0) {
                acc.included.push(module);
            } else {
                acc.excludedCount += 1;
                const reasonSetKey = excludedReasons.sort().join("+");
                acc.excludedReasonSets[reasonSetKey] =
                    (acc.excludedReasonSets[reasonSetKey] || 0) + 1;
            }

            return acc;
        },
        { included: [], excludedCount: 0, excludedReasonSets: {} }
    );
    const transferModulesArray = transferModulesSummaryPartition.included;
    
    const maxRaw =
        deliveryMethodMapped === 1
            ? qualification?.FTMax ?? 0
            : qualification?.PTMax ?? 0;
    const minRaw =
        deliveryMethodMapped === 1
            ? qualification?.FTMin ?? 0
            : qualification?.PTMin ?? 0;
    
    const excludedTransferModulesCount = transferModulesSummaryPartition.excludedCount;
    const minAfterTransferExclusions = Math.max(0, minRaw - excludedTransferModulesCount);
    const maxAfterTransferExclusions = Math.max(0, maxRaw - excludedTransferModulesCount);

    const redoModules = responseData?.redoModules || [];
    const progressionModules = responseData?.progression || [];
    const activeSelectableModulesCount = countActiveRedoProgressionModules(redoModules, progressionModules);

    const max = capMaxByActiveModules(
        maxAfterTransferExclusions,
        redoModules,
        progressionModules
    );

    const min = minAfterTransferExclusions;
    if (process.env.NODE_ENV !== "production") {
        const basisLabel = deliveryMethodMapped === 1 ? "FT" : "PT";
        const transferExcludedDetail = excludedTransferModulesCount
            ? `excluded ${excludedTransferModulesCount}/${allTransferModules.length}`
            : "excluded 0";
        const maxCapDetail = maxAfterTransferExclusions !== max
            ? `capped to active=${activeSelectableModulesCount}`
            : "no active cap";

        console.groupCollapsed(
            `PMC constraints (${basisLabel}) | min raw=${minRaw}, afterTransferExclusions=${minAfterTransferExclusions} | max raw=${maxRaw}, afterTransferExclusions=${maxAfterTransferExclusions}, final=${max} | ${transferExcludedDetail}, ${maxCapDetail}`
        );
        console.debug("1) Base limits from qualification", { minRaw, maxRaw, deliveryMethodMapped, basisLabel });
        console.debug("2) Transfer modules", {
            total: allTransferModules.length,
            included: transferModulesArray.length,
            excluded: excludedTransferModulesCount,
            excludedReasonSets: transferModulesSummaryPartition.excludedReasonSets
        });
        console.debug("3) Limits after transfer exclusions", {
            minAfterTransferExclusions,
            maxAfterTransferExclusions
        });
        console.debug("4) Active selectable redo+progression count", { activeSelectableModulesCount });
        console.debug("5) Final limits used", { min: minAfterTransferExclusions, max });
        console.groupEnd();
    }

    const allModulesArray = Array.isArray(allModules)
        ? allModules
        : Object.values(allModules || {});

    // Include transferModules in the search space for credit modules
    
    const modulesMap = new Map();
    
    allModulesArray.forEach(module => {
        const normalizedId = normalizeModuleId(module.netsuite_product_id);
        if (normalizedId) {
            modulesMap.set(normalizedId, module);
        }
    });
    
    transferModulesArray.forEach(module => {
        const normalizedId = normalizeModuleId(module.netsuite_product_id);
        if (normalizedId) {
            const existingModule = modulesMap.get(normalizedId);
            // Prefer transfer module if it exists, or if existing doesn't have prerequisites but transfer does
            if (!existingModule || 
                (module.pre_requisites && Object.keys(module.pre_requisites).length > 0 && 
                 (!existingModule.pre_requisites || Object.keys(existingModule.pre_requisites).length === 0))) {
                modulesMap.set(normalizedId, module);
            }
        }
    });
    
    const allModulesWithTransfers = Array.from(modulesMap.values());

    const redoModuleIdsFromApi = (responseData?.redoModules || []).map(
        (m) => m.netsuite_product_id
    );
    const progressionModuleIdsFromApi = (responseData?.progression || []).map(
        (m) => m.netsuite_product_id
    );
    const creditIdsFromApi = transferModulesArray
        .filter((m) => m.optional === 0)
        .map((m) => m.netsuite_product_id);

    const getModuleById = (id) => {
        const normalizedId = normalizeModuleId(id);
        return allModulesWithTransfers.find(
            (module) =>
                normalizeModuleId(module.netsuite_product_id) === normalizedId
        );
    };

    // Helper to check if a module is inactive or not eligible (for filtering redo/progression in summary)
    const isInactiveOrNotEligibleForSummary = (module) => {
        if (!module) return false;
        const moduleStatus = (module.module_status || "").toLowerCase().trim();
        const status = (module.status || "").toLowerCase().trim();
        const reason = (module.reason || "").toLowerCase().trim();

        // If a module is ENROLLED, keep it visible in summary even if module_status is "inactive".
        if (status.includes("enrolled") || reason.includes("enrolled")) {
            return false;
        }
        
        const inactivePatterns = ["inactive", "upcoming"];
        const notEligiblePatterns = ["not eligible", "noteligible", "not_eligible", "deregister"];
        
        const checkPatterns = (text, patterns) => patterns.some(p => text.includes(p));
        
        return (
            checkPatterns(moduleStatus, inactivePatterns) ||
            checkPatterns(status, [...inactivePatterns, ...notEligiblePatterns]) ||
            checkPatterns(reason, [...inactivePatterns, ...notEligiblePatterns])
        );
    };

    const hasExistingSelection =
        !!(readOnlyData?.redo || readOnlyData?.progression || readOnlyData?.credit_modules);

    let selectedRedo = [];
    let selectedProgression = [];
    let creditIds = creditIdsFromApi;

    if (readOnly || Boolean(registration?.studentNewRegId) || hasExistingSelection) {
        // In read-only / submitted state, use the stored selection from readOnlyData
        const storedRedo = parseIdList(readOnlyData?.redo);
        const storedProgression = parseIdList(readOnlyData?.progression);
        const storedCredit = parseIdList(readOnlyData?.credit_modules);

      
        selectedRedo = storedRedo;
        selectedProgression = storedProgression;
        creditIds = storedCredit.length ? storedCredit : creditIdsFromApi;
    } else {
        const auto = getPmcAutoSelection({
            redoModules: responseData?.redoModules || [],
            progressionModules: responseData?.progression || [],
            maxModules: max,
            hasExistingSelection,
            readOnly
        });
        selectedRedo = auto.selectedRedo;
        selectedProgression = auto.selectedProgression;
    }

    const seenIds = new Set();
    const items = [
        ...selectedRedo.map((id) => ({ id, type: "redo" })),
        ...selectedProgression.map((id) => ({
            id,
            type: "progression",
        })),
        ...creditIds.map((id) => ({ id, type: "credit" })),
    ]
        .map((item) => {
            const module = getModuleById(item.id);
            if (!module) {
                return null;
            }
            const normalizedId = normalizeModuleId(module.netsuite_product_id);
            if (seenIds.has(normalizedId)) {
                return null;
            }
            // For redo and progression types, exclude inactive/not-eligible modules from summary
            // Credit/adjustment modules are always shown (they represent deductions)
            if ((item.type === "redo" || item.type === "progression") && isInactiveOrNotEligibleForSummary(module)) {
                return null;
            }
            seenIds.add(normalizedId);
            const price = Number(module.price) || 0;
            return {
                id: normalizedId,
                name: module.name,
                type: item.type,
                price,
            };
        })
        .filter(Boolean);

    const modulesCountSelected = items.filter(
        (item) => item.type === "redo" || item.type === "progression"
    ).length;

    // Count available active redo/progression modules using full filtering (inactive, upcoming, not eligible, etc.)
    const availableRedoModules = (responseData?.redoModules || []).filter(m => !isInactiveOrNotEligibleForSummary(m));
    const availableProgressionModules = (responseData?.progression || []).filter(m => !isInactiveOrNotEligibleForSummary(m));
    const availableActiveModuleCount = availableRedoModules.length + availableProgressionModules.length;

    // Cap max to available active modules if available is less than API max
    const rawMaxModules = max || 0;
    const modulesAllowedMax = (rawMaxModules > 0 && availableActiveModuleCount > 0)
        ? Math.min(rawMaxModules, availableActiveModuleCount)
        : rawMaxModules;

    // Also cap min to not exceed the effective max
    const rawModulesRequiredMin = min || 0;
    const modulesRequiredMin = modulesAllowedMax > 0
        ? Math.min(rawModulesRequiredMin, modulesAllowedMax)
        : rawModulesRequiredMin;

    const modulesTotal = items
        .filter((item) => item.type === "redo" || item.type === "progression")
        .reduce((total, item) => total + item.price, 0);

    const adjustmentsTotal = items
        .filter((item) => item.type === "credit")
        .reduce((total, item) => total + item.price, 0);

  

    const totalAmount = modulesTotal - adjustmentsTotal;
    const rawDepositAmount = readOnlyData?.depositAmount;

    let depositAmount = rawDepositAmount || 0;
    if (depositAmount == 0) {
        if (totalAmount <= 0) {
            depositAmount = 0;
        } else if (totalAmount >= 14000) {
            depositAmount = 7000;
        } else {
            depositAmount = Number((totalAmount * 0.5).toFixed(2));
        }
    }
    const responseQualificationType = qualification?.type || "";
    const deliveryMethodKey = (responseQualificationType || "").toLowerCase();
    const deliveryMethodLabel =
        deliveryMethodKey === "parttimeonline"
            ? "Part-Time, Online"
            : deliveryMethodKey === "fulltimeonline"
            ? "Full-Time, Online"
            : deliveryMethodKey === "full"
            ? "Full-Time"
            : responseQualificationType || "";

    return {
        items,
        modulesCountSelected,
        modulesRequiredMin,
        modulesAllowedMax,
        modulesTotal,
        adjustmentsTotal,
        depositAmount,
        totalAmount,
        deliveryMethodLabel,
        qualificationName: qualification.name || ""
    };
};



